import React, { PropsWithChildren, useMemo } from 'react';
import { ToastContainer as ToastifyContainer, toast, type Id } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

type Appearance = 'success' | 'error' | 'warning' | 'info';

interface AddToastOptions {
  appearance?: Appearance;
  autoDismiss?: boolean;
  autoDismissTimeout?: number;
  [key: string]: unknown;
}

const APPEARANCE_TO_TYPE: Record<Appearance, 'success' | 'error' | 'warning' | 'info'> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

/**
 * Drop-in replacement for react-toast-notifications' `useToasts()`, backed by
 * react-toastify. Preserves the `addToast(content, { appearance, autoDismiss })`
 * signature so the existing call sites stay unchanged.
 */
export function useToasts() {
  return useMemo(
    () => ({
      addToast: (
        content: React.ReactNode,
        options: AddToastOptions = {},
        callback?: (id: Id) => void
      ): Id => {
        const { appearance = 'info', autoDismiss, autoDismissTimeout } = options;
        const id = toast(content, {
          type: APPEARANCE_TO_TYPE[appearance] ?? 'info',
          // Fall back to the container default unless a call explicitly opts
          // out of (or overrides) auto-dismiss.
          autoClose: autoDismiss === false ? false : autoDismissTimeout ?? undefined,
        });
        callback?.(id);
        return id;
      },
      removeToast: (id: Id) => toast.dismiss(id),
      removeAllToasts: () => toast.dismiss(),
    }),
    []
  );
}

interface ToastProviderProps {
  autoDismiss?: boolean;
  autoDismissTimeout?: number;
  // Accepted for API compatibility with react-toast-notifications; unused.
  transitionDuration?: number;
  components?: unknown;
  placement?: string;
}

/**
 * Drop-in replacement for react-toast-notifications' `<ToastProvider>`. Renders
 * the app plus a react-toastify container positioned to match the old custom
 * container (top:70 / right:16).
 */
export function ToastProvider({
  children,
  autoDismissTimeout = 5000,
}: PropsWithChildren<ToastProviderProps>) {
  return (
    <>
      {children}
      <ToastifyContainer
        position="top-right"
        autoClose={autoDismissTimeout}
        style={{ top: 70, right: 16, zIndex: 20000 }}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  );
}
